/**
 * Callback.tsx — Auth0 redirect landing.
 *
 * Rebuilt in Pass 7 — only the visual shell changes. All Auth0 + backend
 * handshake logic is preserved exactly from the original.
 */

import { useAuth0 } from '@auth0/auth0-react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdmin } from '../../AdminContext';

const C = {
  bgBase: '#080C18',
  textPrimary: '#E8EFF8',
  textSecondary: '#94A3B8',
  cyan: '#00D4FF',
  cyanGlow: 'rgba(0, 212, 255, 0.35)',
};

export default function Callback() {
  const { isAuthenticated, isLoading, getAccessTokenSilently, error: auth0Error } = useAuth0();
  const navigate = useNavigate();
  const { setUserContext } = useAdmin();

  useEffect(() => {
    if (isLoading) return;

    if (auth0Error) {
      console.error('[Callback] Auth0 SDK error:', auth0Error.message, auth0Error);
    }

    if (isAuthenticated) {
      (async () => {
        try {
          const accessToken = await getAccessTokenSilently();

          const r = await fetch('/api/auth/auth0', {
            method: 'POST',
            headers: { Authorization: `Bearer ${accessToken}` },
          });

          const body = await r.text();

          if (!r.ok) {
            console.error('[Callback] Backend rejected auth:', r.status);
            navigate('/login');
            return;
          }

          const data = JSON.parse(body);
          if (data.success) {
            sessionStorage.setItem('meridian_token', accessToken);
            setUserContext(data.role || 'client_analyst', data.client_name || null);
            navigate('/dashboard');
          } else {
            console.error('[Callback] Auth failed');
            navigate('/login');
          }
        } catch (err) {
          console.error('[Callback] Error:', err);
          navigate('/login');
        }
      })();
    } else {
      navigate('/login');
    }
  }, [isAuthenticated, isLoading, getAccessTokenSilently, navigate, auth0Error, setUserContext]);

  return (
    <div style={{
      background: C.bgBase,
      color: C.textPrimary,
      minHeight: '100vh',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      gap: 24,
      fontFamily: "'DM Sans', -apple-system, system-ui, sans-serif",
    }}>
      {/* Inline keyframes */}
      <style>{`
        @keyframes meridian-spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      <img
        alt="Meridian"
        src="/Meridian_M_logo-transparent.png"
        style={{ height: 40, opacity: 0.9 }}
      />

      <div style={{
        position: 'relative',
        width: 40, height: 40,
      }}>
        <svg width={40} height={40} viewBox="0 0 40 40" style={{
          animation: 'meridian-spin 1s linear infinite',
          filter: `drop-shadow(0 0 6px ${C.cyanGlow})`,
        }}>
          <circle cx={20} cy={20} r={15} fill="none" stroke="rgba(148,163,184,0.15)" strokeWidth={3} />
          <circle cx={20} cy={20} r={15} fill="none" stroke={C.cyan} strokeWidth={3}
            strokeLinecap="round" strokeDasharray="24 70" />
        </svg>
      </div>

      <p style={{
        fontSize: 13, color: C.textSecondary,
        letterSpacing: '0.1em', textTransform: 'uppercase',
      }}>Authenticating…</p>
    </div>
  );
}
